// V3 JOB APPLICANTS ******************************************************************************
// pathname: /v3/{jobSlug}/applicants
// ************************************************************************************************
import { useSwrInfiniteApi } from '@hooks/useSwrApi';


// Interface
// ********************************************
interface UseSwrJobApplicantsProps {
  locale?: string;
  jobSlug: string;
  serverSideData?: ApiSchema<'ResumeResult2'> | ApiSchema<'ErrorResponse'>;
  startIndex?: number;
  limit?: number;
  urlParams?: string;
  successCallback?: (data?: ApiSchema<'ResumeResult2'>[]) => void;
  shouldFetch?: boolean;
}


/**
 * @description SWR: fetch job applicants with infinite pagination.
 *
 * * **locale**: string. What locale to use for the request.
 * * **serverSideData**: ApiTalentPageV3, optional. The data we got from the server side request (if we do it). Default undefined.
 * * **startIndex**: number, optional. The first page number to fetch (ie: fetch starting with page 5). Default 1.
 * * **limit**: number, optional. The number of CVs to get per call. Modifies the *limit* url parameter (&limit=24). Default 24.
 * * **urlParams**: '&sort=relevant', optional. Default empty string.
 * * **successCallback**: function, optional. A function to call when the data is successfully fetched. Default undefined.
 * * **shouldFetch**: boolean, optional. If true, the data will be fetched. Default true.
 */
export const useSwrJobApplicants = (props: UseSwrJobApplicantsProps) => {
  // Props destructuring
  // ********************************************
  const {
    locale,
    jobSlug,
    serverSideData,
    startIndex = 1,
    limit = 24,
    urlParams = '',
    successCallback,
    shouldFetch = true,
  } = props;


  // Get data from API
  // ********************************************
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    mutateSiblings,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiSchema<'ResumeResult2'>>({
    url: `/v3/${jobSlug}/applicants`,
    locale,
    urlParams,
    serverSideData,
    shouldFetch,
    infiniteConfig: {
      paginationType: 'page',
      limit,
      // the key where the array of items is located in the response
      responseArrayKey: 'items',
      startIndex,
    },
    apiOptions: {
      keepPreviousData: true,
      revalidateOnFocus: false,
      dedupingInterval: 2000, // 2 seconds
      parallel: true,
      onSuccess: (newData) => {
        if (successCallback) {
          successCallback(newData);
        }
      },
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });


  // Return data
  // ********************************************
  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    mutateSiblings,
    isEmpty,
    // We need to account for the case when the data the last page perfectly fits the limit, so we use the pages property
    // if we reached the end (page === pages), we don't show the load more button (showLoadMore = false)
    showLoadMore: showLoadMore && data?.at(-1)?.page !== data?.at(-1)?.pages,
    sizeAsRef,
  };
};
